"use client";
import React from "react";

import NumberedPagination from "./NumberedPagination";

type PaginationDirection = "back" | "forward";

export interface PaginationProps {
  continuous?: boolean;
  count: number;
  hideNextPrevButtons?: boolean | [boolean, boolean, boolean];
  onPageChange: (index: number, direction: PaginationDirection) => void;
  page: number;
  siblingCount?: number;
  variant?: "dotted" | "numbered";
  disabled?: boolean;
}

export interface FinalPaginationProps {
  continuous: boolean;
  count: number;
  handleClickNext: () => void;
  handleClickPrev: () => void;
  handleKeyUp: (e: React.KeyboardEvent) => void;
  handlePageChange: (page: number) => void;
  hasNext: boolean;
  hasPrev: boolean;
  hideNextPrevButtonsArray: [boolean, boolean, boolean];
  page: number;
  disabled: boolean;
}

export const Pagination: React.FC<PaginationProps> = ({
  continuous = false,
  count,
  hideNextPrevButtons = false,
  onPageChange,
  page,
  siblingCount,
  disabled = false
}) => {
  const hasNext = page < count;
  const hasPrev = page > 1;
  const hideNextPrevButtonsArray = React.useMemo<
    [boolean, boolean, boolean]
  >(() => {
    if (Array.isArray(hideNextPrevButtons)) {
      return hideNextPrevButtons;
    } else {
      return [hideNextPrevButtons, hideNextPrevButtons, hideNextPrevButtons];
    }
  }, [hideNextPrevButtons]);
  const handlePageChange = React.useCallback(
    (pageNext: number) => {
      if (pageNext === page) {
        return;
      } else if (continuous) {
        if (pageNext < 1) {
          onPageChange(count, "back");
        } else if (pageNext > count) {
          onPageChange(1, "forward");
        } else {
          onPageChange(pageNext, pageNext > page ? "forward" : "back");
        }
      } else if (pageNext >= 1 && pageNext <= count) {
        onPageChange(pageNext, pageNext > page ? "forward" : "back");
      }
    },
    [continuous, count, onPageChange, page]
  );
  const handleClickNext = React.useCallback(
    () => handlePageChange(page + 1),
    [handlePageChange, page]
  );
  const handleClickPrev = React.useCallback(
    () => handlePageChange(page - 1),
    [handlePageChange, page]
  );

  const handleKeyUp = React.useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case "ArrowLeft":
          handleClickPrev();
          break;
        case "ArrowRight":
          handleClickNext();
          break;
      }
    },
    [handleClickPrev, handleClickNext]
  );

  if (count <= 0) {
    return null;
  }

  const props: FinalPaginationProps = {
    continuous,
    count,
    handleClickNext,
    handleClickPrev,
    handleKeyUp,
    handlePageChange,
    hasNext,
    hasPrev,
    hideNextPrevButtonsArray,
    page,
    disabled
  };

  return (
    <NumberedPagination
      {...props}
      siblingCount={siblingCount}
      disabled={disabled}
    />
  );
};
